import type { CSSProperties, JSX } from 'react';
import { useTranslation } from '../i18n';

export type Status = 'up' | 'down' | 'warn' | 'unknown';

/** Ключи подписей состояний: подпись читает скринридер, и читает на своём языке. */
const LABEL_KEYS: Record<Status, string> = {
  up: 'common.status.up',
  down: 'common.status.down',
  warn: 'common.status.warn',
  unknown: 'common.status.unknown',
};

export interface StatusDotProps {
  status: Status;
  /** Размер стороны в пикселях. 9 — в плотных таблицах, 11 — в списках. */
  size?: number;
  /** Подпись для скринридера. По умолчанию — название состояния. */
  label?: string;
}

/**
 * Индикатор состояния: форма плюс цвет.
 *
 * ТЗ §12 требует, чтобы статус кодировался не только цветом — иначе индикатор
 * бесполезен для дальтоников. Формы взяты из макета NetDeck.html:
 * заполненный круг — работает, круг с крестом — не работает,
 * полый круг — неизвестно, круг с восклицанием — частично.
 */
export function StatusDot({ status, size = 9, label }: StatusDotProps): JSX.Element {
  const { t } = useTranslation();

  const title = label ?? t(LABEL_KEYS[status]);
  const base: CSSProperties = {
    width: size,
    height: size,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
    fontFamily: 'var(--font-mono)',
    fontSize: Math.round(size * 0.82),
    lineHeight: 1,
  };

  const style: CSSProperties =
    status === 'up'
      ? { ...base, background: 'var(--nd-up)' }
      : status === 'down'
        ? { ...base, border: '1.5px solid var(--nd-down)', color: 'var(--nd-down)' }
        : status === 'warn'
          ? { ...base, background: 'var(--nd-warn)', color: 'var(--nd-surface-raised)' }
          : { ...base, border: '1.5px solid var(--nd-text-dim)' };

  return (
    <span role="img" aria-label={title} title={title} style={style}>
      {status === 'down' ? '×' : status === 'warn' ? '!' : ''}
    </span>
  );
}
